<template>
  <div class="fi-login">
    <div v-if="!resetting_password" class="fi-card">
      <!-- brand head -->
      <div class="fi-head">
        <div class="fi-logo">
          <svg viewBox="-2 0 200 128" fill="none" role="img" aria-label="Fleet Integrations">
            <g stroke="#1976D2" stroke-width="8" stroke-linecap="round" stroke-linejoin="round">
              <path d="M36 120 C18 120 4 106 4 88 C4 74 14 62 28 57 C26 42 36 30 52 30 C58 30 64 32 68 36 C76 18 92 8 110 8 C138 8 160 30 162 57 C180 60 192 76 192 92 C192 108 178 120 162 120 Z"/>
              <path d="M65 64 H133 M116 48 L133 64 L116 80"/>
              <path d="M133 92 H65 M82 76 L65 92 L82 108"/>
            </g>
          </svg>
        </div>
        <h1 class="fi-title">Fleet Integrations</h1>
        <p class="fi-subtitle">Sign in to your portal</p>
      </div>

      <!-- login form -->
      <form class="fi-form" @submit.prevent="login">
        <div class="fi-field">
          <label class="fi-label" for="username">Email</label>
          <div class="fi-input" :class="{ 'is-error': errored }">
            <svg class="fi-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>
            <input
              id="username"
              v-model="form.username"
              type="text"
              required
              autocomplete="username"
              placeholder="you@company.com"
            />
          </div>
        </div>

        <div class="fi-field">
          <label class="fi-label" for="password">Password</label>
          <div class="fi-input" :class="{ 'is-error': errored }">
            <svg class="fi-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="9" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>
            <input
              id="password"
              v-model="form.password"
              :type="showPassword ? 'text' : 'password'"
              required
              autocomplete="current-password"
              placeholder="Enter your password"
              class="fs-exclude"
            />
            <button type="button" class="fi-eye" @click="showPassword = !showPassword" :aria-label="showPassword ? 'Hide password' : 'Show password'">
              <svg v-if="!showPassword" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
              <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 3l18 18"/><path d="M10.6 10.6a3 3 0 0 0 4.2 4.2"/><path d="M9.9 5.2A9.5 9.5 0 0 1 12 5c6.5 0 10 7 10 7a13.7 13.7 0 0 1-2.4 3.2M6.1 6.1A13.7 13.7 0 0 0 2 12s3.5 7 10 7a9.3 9.3 0 0 0 3-.5"/></svg>
            </button>
          </div>
        </div>

        <div class="fi-row-end">
          <b-link class="fi-link" @click="auth_navigation(true)">Forgot password?</b-link>
        </div>

        <transition name="fi-fade">
          <div v-if="errored" class="fi-error">
            <svg class="fi-ic-sm" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z"/></svg>
            <span>{{ alert.message }}</span>
          </div>
        </transition>

        <button id="login-button" class="fi-submit" type="submit" :disabled="loading">
          <span v-if="loading" class="fi-spinner" aria-hidden="true"></span>
          <span>{{ loading ? 'Signing in&hellip;' : 'Sign in' }}</span>
        </button>
      </form>

      <p class="fi-register">
        New to Fleet Integrations?
        <router-link class="fi-link" :to="{ path: '/register' }">Register your company</router-link>
      </p>

      <div class="fi-foot">
        <div class="fi-foot-links">
          <a class="fi-foot-link" target="_new" href="https://www.fleet-integrations.com">Learn more</a>
          <span class="fi-dot">&bull;</span>
          <a class="fi-foot-link" target="_new" href="mailto:support@initekconsulting.com">Contact us</a>
        </div>
        <a class="fi-powered" target="_new" href="https://www.initekconsulting.com/fleet-integrations">
          <span>powered by</span>
          <img height="18" src="/initek-logo.png" alt="Initek" />
        </a>
      </div>
    </div>

    <!-- forgot-password sub-view, kept in the same centered card -->
    <div v-else class="fi-card">
      <ForgotPassword @auth_navigation="auth_navigation" />
    </div>
  </div>
</template>

<script>
    import axios from 'axios'
    import {mapMutations} from "vuex";
    import ForgotPassword from './forgot_password'

    export default {
        name: 'login',
        components: { ForgotPassword },
        data() {
            return {
                loading: false,
                errored: false,
                form: { username:'', password:''},
                alert: { show:false, message:'', variant:''},
                resetting_password: false,
                showPassword: false,
                jwt: null,
            }
        },
        created(){
          localStorage.clear()
          this.$emit('authenticated', false)
        },
        methods: {
          auth_navigation(bool){
            this.resetting_password = bool
          },
            async login() {
                if(this.form.username != "" && this.form.password != "") {
                    this.loading = true
                    this.errored = false
                    axios.post(
                        process.env.VUE_APP_API_URL + "bookings/auth/login",
                        this.form,
                        {headers: {'Access-Control-Allow-Origin': '*'}}
                    ).then((response) => {
                      this.handle_login_response(response.data);
                    }).catch(error => {
                      if (error.message && error.message == "Network Error")
                         this.alert = {show: true, message: "system in currently unavailable, try again later or contact support", variant: 'danger'}
                      else
                        this.alert = {show: true, message: "unable to login, check username & password or contact support", variant: 'danger'}
                      this.errored = true
                      this.loading = false
                    });
                }
            },
            async handle_login_response(data) {
                setTimeout(() => {
                    localStorage.setItem('user', JSON.stringify(data.data.user))
                    localStorage.setItem('jwt', data.data.token)
                    localStorage.setItem('authenticated', true)

                    const jwt_data = this.h_get_jwt_data(data.data.token);
                    localStorage.setItem('expiration_token_time', jwt_data.exp);

                    this.set_user_data(data.data.user)
                    // FI-1871: apply the user's saved site theme on login.
                    this.set_site_theme(data.data.user.preferences && data.data.user.preferences.site_theme)
                    setTimeout(() => {
                        this.$emit('authenticated', true)
                        if (this.$route.params.nextUrl != null) {
                            var nextUrl = this.$route.params.nextUrl
                            this.$router.push(nextUrl.startsWith('/') ? nextUrl : '/' + nextUrl)
                        } else if (data.data.user.role === 'driver' &&
                                   data.data.user.tenant === 'dattco-413f464d') {
                          this.$router.push({ name: 'driver_bid_board_tbn' })
                        } else if (data.data.user.home_page) {
                          this.$router.push(data.data.user.home_page)
                        } else if (data.data.user.role === 'booking-customer') {
                          // Booking-customer users should always land on booking portal list.
                          this.$router.push('/booking-customer')
                        } else {
                            this.$router.push('dashboard')
                        }
                        this.loading = false
                    }, 500)
                }, 500)
            },
           ...mapMutations('profile', [ 'set_user_data', 'set_site_theme' ]),
        }
    }
</script>

<style scoped>
/* fresh login, drawn from the mobile apps' design language */
.fi-login {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  color: #0a0d14;
  background:
    radial-gradient(900px 500px at 50% -8%, rgba(25,118,210,0.12), transparent 60%),
    radial-gradient(700px 500px at 100% 110%, rgba(25,118,210,0.07), transparent 55%),
    linear-gradient(180deg, #f7f9fc 0%, #eef2f7 100%);
}

.fi-card {
  width: 100%;
  max-width: 408px;
  background: #fff;
  border: 1px solid rgba(16,24,40,0.06);
  border-radius: 18px;
  box-shadow: 0 18px 50px rgba(16,24,40,0.10), 0 2px 6px rgba(16,24,40,0.04);
  padding: 40px 36px 28px;
  box-sizing: border-box;
}

/* head */
.fi-head { text-align: center; margin-bottom: 26px; }
.fi-logo { width: 66px; margin: 0 auto 16px; }
.fi-logo svg { width: 100%; height: auto; display: block; }
.fi-title {
  margin: 0;
  font-size: 23px;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #1976D2;
}
.fi-subtitle { margin: 4px 0 0; font-size: 14.5px; color: #5b6470; }

/* form */
.fi-form { display: flex; flex-direction: column; gap: 16px; }
.fi-field { display: flex; flex-direction: column; gap: 7px; }
.fi-label { font-size: 13px; font-weight: 600; color: #3a4250; text-align: left; }

.fi-input {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding: 0 14px;
  background: #f5f7fa;
  border: 1.5px solid #e3e8ef;
  border-radius: 11px;
  transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
}
.fi-input:focus-within {
  background: #fff;
  border-color: #1976D2;
  box-shadow: 0 0 0 4px rgba(25,118,210,0.12);
}
.fi-input.is-error {
  border-color: #e0796f;
  box-shadow: 0 0 0 4px rgba(192,57,43,0.10);
}
.fi-input .fi-ic { width: 18px; height: 18px; color: #98a2b3; flex: none; transition: color .15s ease; }
.fi-input:focus-within .fi-ic { color: #1976D2; }
.fi-input input {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  outline: 0;
  background: transparent;
  /* 16px minimum: ios safari auto-zooms focused inputs below 16px */
  font-size: 16px;
  color: #0a0d14;
}
.fi-input input::placeholder { color: #a4abb8; }

.fi-eye {
  flex: none;
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  margin-right: -6px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #98a2b3;
  cursor: pointer;
}
.fi-eye:hover { color: #5b6470; }
.fi-eye svg { width: 19px; height: 19px; }

.fi-row-end { display: flex; justify-content: flex-end; margin-top: -4px; }

.fi-link {
  color: #1976D2;
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}
.fi-link:hover { text-decoration: underline; color: #1565C0; }

.fi-error {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 10px 12px;
  background: #fdecea;
  color: #b5372a;
  border-radius: 10px;
  font-size: 13.5px;
  line-height: 1.35;
}
.fi-error .fi-ic-sm { width: 18px; height: 18px; flex: none; }

.fi-submit {
  position: relative;
  height: 48px;
  margin-top: 4px;
  border: 0;
  border-radius: 11px;
  background: linear-gradient(180deg, #1f86e6 0%, #1565C0 100%);
  color: #fff;
  font-size: 15.5px;
  font-weight: 650;
  letter-spacing: 0.01em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 8px 18px rgba(25,118,210,0.28);
  transition: filter .15s ease, transform .04s ease, box-shadow .15s ease;
}
.fi-submit:hover:not(:disabled) { filter: brightness(1.05); }
.fi-submit:active:not(:disabled) { transform: translateY(1px); }
.fi-submit:disabled { opacity: 0.65; cursor: default; box-shadow: none; }

.fi-spinner {
  width: 17px;
  height: 17px;
  border: 2.5px solid rgba(255,255,255,0.45);
  border-top-color: #fff;
  border-radius: 50%;
  animation: fi-spin 0.7s linear infinite;
}
@keyframes fi-spin { to { transform: rotate(360deg); } }

.fi-register {
  margin: 20px 0 0;
  text-align: center;
  font-size: 13.5px;
  color: #5b6470;
}

.fi-foot {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #eef1f5;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.fi-foot-links { display: flex; align-items: center; gap: 10px; }
.fi-foot-link { color: #5b6470; font-size: 13px; font-weight: 500; text-decoration: none; }
.fi-foot-link:hover { color: #1976D2; }
.fi-dot { color: #c2c9d4; font-size: 12px; }
.fi-powered {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: #98a2b3;
  text-decoration: none;
}
.fi-powered img { opacity: 0.9; }

/* error fade */
.fi-fade-enter-active, .fi-fade-leave-active { transition: opacity .2s ease; }
.fi-fade-enter, .fi-fade-leave-to { opacity: 0; }

@media (max-width: 440px) {
  .fi-card { padding: 32px 22px 24px; border-radius: 16px; }
}
</style>
